import React, { useEffect, useRef, useState } from 'react';
import { Bell, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

const typeColor = {
  new_lead: 'bg-sky-400', lead_assigned: 'bg-primary', sales_assigned: 'bg-emerald-400',
  appointment: 'bg-violet-400', follow_up: 'bg-amber-400', sales_assignment_needed: 'bg-orange-400',
};

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 60000;
  if (diff < 1) return 'agora';
  if (diff < 60) return `${Math.floor(diff)}min`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h`;
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
};

export const NotificationCenter = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try { const { data } = await api.get('/notifications'); setItems(data.items); setUnread(data.unread); } catch (e) {}
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => { clearInterval(iv); document.removeEventListener('mousedown', onClick); };
  }, []);

  const openItem = async (n) => {
    try { await api.put(`/notifications/${n.id}/read`); } catch (e) {}
    setOpen(false);
    load();
    if (n.related_type === 'bdc_lead') navigate(`/app/bdc?lead=${n.related_id}`);
  };

  const readAll = async () => {
    try { await api.put('/notifications/read-all'); load(); } catch (e) {}
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notif-bell" onClick={() => setOpen((o) => !o)} className="relative w-9 h-9 rounded-lg border border-border flex items-center justify-center text-foreground/65 hover:text-foreground hover:border-foreground/25 transition-colors">
        <Bell className="w-4 h-4" />
        {unread > 0 && <span data-testid="notif-badge" className="absolute -top-1.5 -right-1.5 bg-primary text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <div data-testid="notif-dropdown" className="absolute right-0 mt-2 w-[380px] max-w-[90vw] rounded-xl border border-border bg-card shadow-2xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/70">
            <p className="font-display font-black text-sm uppercase tracking-wide">Notificações</p>
            {unread > 0 && <button data-testid="notif-read-all" onClick={readAll} className="flex items-center gap-1 text-[11px] text-primary hover:text-foreground transition-colors"><Check className="w-3 h-3" /> Marcar todas lidas</button>}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 && <p className="text-sm text-muted-foreground/70 text-center py-10">Nenhuma notificação.</p>}
            {items.map((n) => (
              <button key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => openItem(n)} className={`w-full text-left px-4 py-3 border-b border-border/70 hover:bg-muted/40 transition-colors ${!n.read ? 'bg-primary/[0.06]' : ''}`}>
                <div className="flex items-start gap-2.5">
                  <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${!n.read ? (typeColor[n.type] || 'bg-primary') : 'bg-foreground/15'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className={`text-[12px] font-bold uppercase tracking-wide ${!n.read ? 'text-foreground' : 'text-foreground/60'}`}>{n.title}</p>
                      <span className="text-[10px] text-muted-foreground/60 shrink-0">{fmtWhen(n.created_at)}</span>
                    </div>
                    <p className="text-[12px] text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
